// Vector source, so the 64px canvas stays sharp on retina tab bars.
const ICON_SOURCE = "/static/icons/favicon-light.svg?v=20260921"
const SIZE = 64
// A true red: the app's rose reads pink on a tinted tab bar.
const DOT_COLOR = "#ef4444"
const DOT_RADIUS = SIZE * 0.16
const CUTOUT = SIZE * 0.08

const ORIGINAL_HREF = "data-original-href"
const ORIGINAL_TYPE = "data-original-type"

/** Points every tab icon at `href`, or back at the shipped icon when null. */
export function setFaviconHref(doc: Document, href: string | null) {
	for (const link of doc.querySelectorAll<HTMLLinkElement>(
		'link[rel="icon"]',
	)) {
		if (!link.hasAttribute(ORIGINAL_HREF)) {
			link.setAttribute(ORIGINAL_HREF, link.getAttribute("href") ?? "")
			link.setAttribute(ORIGINAL_TYPE, link.getAttribute("type") ?? "")
		}
		const originalType = link.getAttribute(ORIGINAL_TYPE)
		link.setAttribute("href", href ?? link.getAttribute(ORIGINAL_HREF) ?? "")
		if (href) link.setAttribute("type", "image/png")
		else if (originalType) link.setAttribute("type", originalType)
		else link.removeAttribute("type")
	}
}

/** Draws the icon with a red dot top right, cut out from the icon so it reads on light and dark tab bars. */
export function drawBadgedFavicon(
	icon: CanvasImageSource,
	canvas: HTMLCanvasElement,
): string | null {
	canvas.width = SIZE
	canvas.height = SIZE
	const ctx = canvas.getContext("2d")
	if (!ctx) return null
	const cx = SIZE - DOT_RADIUS
	const cy = DOT_RADIUS

	ctx.clearRect(0, 0, SIZE, SIZE)
	ctx.drawImage(icon, 0, 0, SIZE, SIZE)
	ctx.globalCompositeOperation = "destination-out"
	ctx.beginPath()
	ctx.arc(cx, cy, DOT_RADIUS + CUTOUT, 0, Math.PI * 2)
	ctx.fill()
	ctx.globalCompositeOperation = "source-over"
	ctx.fillStyle = DOT_COLOR
	ctx.beginPath()
	ctx.arc(cx, cy, DOT_RADIUS, 0, Math.PI * 2)
	ctx.fill()
	return canvas.toDataURL("image/png")
}

let badged: Promise<string | null> | null = null

/** Badged icon as a data URL, drawn once per page load. Null keeps the plain icon. */
export function loadBadgedFavicon(): Promise<string | null> {
	badged ??= new Promise<string | null>((resolve) => {
		const icon = new Image()
		icon.onload = () =>
			resolve(drawBadgedFavicon(icon, document.createElement("canvas")))
		icon.onerror = () => resolve(null)
		icon.src = ICON_SOURCE
	}).then((href) => {
		if (!href) badged = null
		return href
	})
	return badged
}
